import { useMemo } from "react";

import clsx from "clsx";
import { isNil, keyBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import {
  INHERIT_BUDGET_MIX_KPI,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { getFractionChanged } from "@features/mmm/utils/calculators";
import {
  calculateCac,
  calculateRoas,
  type FullBudgetMixResponse,
} from "@north-beam/nb-common";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { ListBox } from "@shared/list-box";
import { renderChevronAnimation } from "@shared/selects/utils";

import { type ClientSetting, KpiType } from "../client-setting/types";
import { IncrementalEfficiencyExplainer } from "../scenario-modeling/scenario-modeling-content";

import { type HistoricalMetricComparisonData } from "./card";
import { MetricBoxTooltip } from "./metric-box-tooltip";
import {
  getMetricColor,
  MetricBoxDirectionArrow,
  PercentageChangeChip,
  useMetricComparisonDate,
} from "./title-section/metric-box";

export type MetricBoxOption<T> = {
  key: T;
  displayLabel: string;
  displayValue: React.ReactNode;
  shouldShowAsOption: boolean;
  type: "spend" | KpiType;
};

export type CompareBudgetData = {
  metric: string;
  metricChangePercentage: number;
  historicalValue: number;
  shouldError: boolean;
  isDesiredDirection: boolean;
};

type Props<T> = {
  budgetMix?: FullBudgetMixResponse;
  className?: string;
  clientSetting?: ClientSetting;
  dateRange?: { startDate: string; endDate: string };
  historicalComparisonData?: HistoricalMetricComparisonData;
  isHistorical?: boolean;
  options: MetricBoxOption<T>[];
  value: T;
};

export function useExtractKPI({
  clientSetting,
  budgetMix,
}: {
  clientSetting?: ClientSetting;
  budgetMix?: FullBudgetMixResponse;
}) {
  const { kpi: clientSettingKpi } = useClientSettingData(clientSetting);

  const { kpis: clientKpis } = useClientKpis();
  const { selectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  const kpiMetric = useMemo(() => {
    const keyClientKpis = keyBy(clientKpis, "kpi_name");
    if (selectedBudgetMixKpiId === INHERIT_BUDGET_MIX_KPI) {
      if (clientSetting) {
        return clientSettingKpi;
      }

      if (budgetMix) {
        return keyClientKpis[budgetMix.data.kpi_name ?? ""];
      }
      return clientKpis.find((kpi) => kpi.is_default_kpi);
    }

    return clientKpis.find((kpi) => kpi.id === selectedBudgetMixKpiId);
  }, [
    clientKpis,
    selectedBudgetMixKpiId,
    clientSetting,
    budgetMix,
    clientSettingKpi,
  ]);

  return kpiMetric;
}

export function MetricHeader<T>({
  budgetMix,

  clientSetting,
  dateRange,
  historicalComparisonData,
  isHistorical = false,

  options,
  value,
}: Props<T>) {
  const selectedMetric = useMemo(
    () => options.find((opt) => opt.key === value) || options[0],
    [options, value],
  );

  const kpiMetric = useExtractKPI({ budgetMix, clientSetting });

  const compareData: CompareBudgetData = useMemo(() => {
    if (selectedMetric.key === "spend") {
      const historicalValue = historicalComparisonData?.spend ?? 0;
      return {
        metric: "spend",
        metricChangePercentage:
          getFractionChanged({
            current: budgetMix?.data.aggregated_data.summary_data.spend ?? 0,
            historical: historicalValue,
          }) * 100,
        historicalValue,
        shouldError: false,
        isDesiredDirection: true,
      };
    } else if (!budgetMix) {
      return {
        metric: "cac",
        metricChangePercentage: 0,
        historicalValue: 0,
        shouldError: false,
        isDesiredDirection: false,
      };
    }

    const historicalValue =
      historicalComparisonData?.kpi_data &&
      historicalComparisonData.kpi_data[kpiMetric?.kpi_name || ""];
    const currentValueRange =
      budgetMix?.data.aggregated_data.summary_data.kpi_data[
        kpiMetric?.kpi_name || ""
      ]?.values;

    const historicalSpend = historicalComparisonData?.spend;

    const shouldError =
      isNil(historicalValue) ||
      isNil(currentValueRange) ||
      isNil(historicalSpend);
    if (kpiMetric?.kpi_type === KpiType.REVENUE) {
      if (shouldError) {
        return {
          metric: "roas",
          metricChangePercentage: 0,
          historicalValue: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentKpiValue = currentValueRange.median;
      const historicalRoas = calculateRoas({
        revenue: historicalValue,
        spend: historicalSpend,
      });
      const currentRoas = calculateRoas({
        revenue: currentKpiValue,
        spend: budgetMix.data.aggregated_data.summary_data.spend,
      });
      const metricChangePercentage =
        getFractionChanged({
          current: currentRoas,
          historical: historicalRoas,
        }) * 100;
      return {
        metric: "roas",
        metricChangePercentage,
        historicalValue: historicalRoas,
        isDesiredDirection: metricChangePercentage > 0,
        shouldError: false,
      };
    } else {
      if (shouldError) {
        return {
          metric: "cac",
          metricChangePercentage: 0,
          historicalValue: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentKpiValue = currentValueRange.median;
      const historicalCac = calculateCac({
        transactions: historicalValue,
        spend: historicalSpend,
      });
      const currentCac = calculateCac({
        transactions: currentKpiValue,
        spend: budgetMix.data.aggregated_data.summary_data.spend,
      });
      const metricChangePercentage =
        getFractionChanged({ current: currentCac, historical: historicalCac }) *
        100;
      return {
        metric: "cac",
        metricChangePercentage,
        historicalValue: historicalCac,
        shouldError: false,
        isDesiredDirection: metricChangePercentage < 0,
      };
    }
  }, [historicalComparisonData, selectedMetric, budgetMix, kpiMetric]);

  const { text, arrowType, color } = useMetricComparisonDate({ compareData });

  return (
    <div
      className={clsx(
        "w-full p-1 pt-2.5 flex flex-col items-center",
        getMetricColor({
          inverseColor: false,
          color,
          isNeutral: false,
        }),
      )}
    >
      <div className="flex justify-center items-center flex-row  ">
        {budgetMix && compareData && !compareData.shouldError && (
          <>
            {getMetricTitle(selectedMetric)}
            <span className="mr-2" />
            {arrowType ? (
              <i
                className={clsx("fa-regular text-xs mr-1 ", {
                  "fa-arrow-up": arrowType === MetricBoxDirectionArrow.UP,
                  "fa-arrow-down": arrowType === MetricBoxDirectionArrow.DOWN,
                })}
              ></i>
            ) : (
              // placeholder
              <span></span>
            )}
            {text}
            <MetricBoxTooltip
              selectedMetric={selectedMetric}
              compareData={compareData}
              dateRange={dateRange}
              isHistorical={isHistorical}
            >
              <i className="fa-regular fa-circle-info ml-1 text-xs" />
            </MetricBoxTooltip>
          </>
        )}
      </div>
    </div>
  );
}

export function MetricBox<T>({
  budgetMix,
  className,
  clientSetting,
  dateRange,
  historicalComparisonData,
  isHistorical = false,

  options,
  value,
}: Props<T>) {
  const selectedMetric = useMemo(
    () => options.find((opt) => opt.key === value) || options[0],
    [options, value],
  );

  const kpiMetric = useExtractKPI({ budgetMix, clientSetting });

  const compareData: CompareBudgetData = useMemo(() => {
    if (selectedMetric.key === "spend") {
      const historicalValue = historicalComparisonData?.spend ?? 0;
      return {
        metric: "spend",
        metricChangePercentage:
          getFractionChanged({
            current: budgetMix?.data.aggregated_data.summary_data.spend ?? 0,
            historical: historicalValue,
          }) * 100,
        historicalValue,
        shouldError: false,
        isDesiredDirection: true,
      };
    } else if (!budgetMix) {
      return {
        metric: "cac",
        metricChangePercentage: 0,
        historicalValue: 0,
        shouldError: false,
        isDesiredDirection: false,
      };
    }

    const historicalValue =
      historicalComparisonData?.kpi_data &&
      historicalComparisonData.kpi_data[kpiMetric?.kpi_name || ""];
    const currentValueRange =
      budgetMix?.data.aggregated_data.summary_data.kpi_data[
        kpiMetric?.kpi_name || ""
      ]?.values;

    const historicalSpend = historicalComparisonData?.spend;

    const shouldError =
      isNil(historicalValue) ||
      isNil(currentValueRange) ||
      isNil(historicalSpend);
    if (kpiMetric?.kpi_type === KpiType.REVENUE) {
      if (shouldError) {
        return {
          metric: "roas",
          metricChangePercentage: 0,
          historicalValue: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentKpiValue = currentValueRange.median;
      const historicalRoas = calculateRoas({
        revenue: historicalValue,
        spend: historicalSpend,
      });
      const currentRoas = calculateRoas({
        revenue: currentKpiValue,
        spend: budgetMix.data.aggregated_data.summary_data.spend,
      });
      const metricChangePercentage =
        getFractionChanged({
          current: currentRoas,
          historical: historicalRoas,
        }) * 100;
      return {
        metric: "roas",
        metricChangePercentage,
        historicalValue: historicalRoas,
        isDesiredDirection: metricChangePercentage > 0,
        shouldError: false,
      };
    } else {
      if (shouldError) {
        return {
          metric: "cac",
          metricChangePercentage: 0,
          historicalValue: 0,
          shouldError: true,
          isDesiredDirection: false,
        };
      }
      const currentKpiValue = currentValueRange.median;
      const historicalCac = calculateCac({
        transactions: historicalValue,
        spend: historicalSpend,
      });
      const currentCac = calculateCac({
        transactions: currentKpiValue,
        spend: budgetMix.data.aggregated_data.summary_data.spend,
      });
      const metricChangePercentage =
        getFractionChanged({ current: currentCac, historical: historicalCac }) *
        100;
      return {
        metric: "cac",
        metricChangePercentage,
        historicalValue: historicalCac,
        shouldError: false,
        isDesiredDirection: metricChangePercentage < 0,
      };
    }
  }, [historicalComparisonData, selectedMetric, budgetMix, kpiMetric]);

  const title = getMetricTitle(selectedMetric);

  const showTooltip =
    selectedMetric.type === KpiType.REVENUE ||
    selectedMetric.type === KpiType.TRANSACTIONS;

  return (
    <div
      className={clsx(
        "pl-3.5 pr-6 pt-2.5 pb-1.5 flex-grow relative",
        className,
      )}
    >
      <div className="flex justify-start items-center">
        <div className={clsx("flex items-center")}>
          <div className="text-2xs font-semibold truncate tracking-tight mb-0.5">
            <GenericTooltip
              content={
                <div>
                  <div className="font-semibold mb-1">
                    {selectedMetric.displayLabel}
                  </div>
                  {(selectedMetric.type === KpiType.REVENUE ||
                    selectedMetric.type === KpiType.TRANSACTIONS) && (
                    <IncrementalEfficiencyExplainer
                      efficiencyKpiType={
                        selectedMetric.type === KpiType.REVENUE ? "ROAS" : "CAC"
                      }
                    />
                  )}
                </div>
              }
              showTooltip={showTooltip}
              noInfoCircle={!showTooltip}
            >
              {title}
            </GenericTooltip>
          </div>
        </div>
        {budgetMix && compareData && !compareData.shouldError && (
          <MetricBoxTooltip
            className={clsx("absolute-vertical-center right-1")}
            selectedMetric={selectedMetric}
            compareData={compareData}
            dateRange={dateRange}
            isHistorical={isHistorical}
          >
            <PercentageChangeChip
              compareData={compareData}
              isNeutral={selectedMetric.key === "spend"}
            />
          </MetricBoxTooltip>
        )}
      </div>
      <div className="text-cosmic text-sm font-semibold whitespace-nowrap -mt-0.5">
        {selectedMetric.displayValue}
      </div>
    </div>
  );
}

export function getMetricTitle(selectedMetric: MetricBoxOption<unknown>) {
  if (selectedMetric.type === "spend") {
    return "Spend";
  }
  return `${
    {
      [KpiType.REVENUE]: "ROAS",
      [KpiType.TRANSACTIONS]: "CAC",
      [KpiType.CUSTOMERS]: "Customers",
      spend: selectedMetric.displayLabel,
    }[selectedMetric.type]
  } of ${selectedMetric.displayLabel}`;
}
